import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Newspaper, Save, RefreshCw, Trash2, Crown, BarChart2 } from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'simons', label: 'Simons Analizi', icon: Crown, endpoint: '/bulletin/simons' },
  { key: 'bist',   label: 'BIST Analizi',   icon: BarChart2, endpoint: '/bulletin/bist' },
];

const emptyForm = {
  reference_symbol: '',
  similar_symbol: '',
  admin_comment: '',
  interval: '1wk',
  start_date: '',
  end_date: '',
};

const BulletinForm = ({ bulletinType, getAuthHeader }) => {
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [bulletin, setBulletin]   = useState(null);
  const [form, setForm]           = useState(emptyForm);

  const fetchBulletin = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/bulletin/${bulletinType}`, { headers: getAuthHeader() });
      if (res.data) {
        setBulletin(res.data);
        setForm({
          reference_symbol: res.data.reference_symbol || '',
          similar_symbol:   res.data.similar_symbol   || '',
          admin_comment:    res.data.admin_comment     || '',
          interval:         res.data.interval          || '1wk',
          start_date:       res.data.start_date        || '',
          end_date:         res.data.end_date          || '',
        });
      } else {
        setBulletin(null);
        setForm(emptyForm);
      }
    } catch (err) {
      if (err.response?.status !== 404) toast.error('Bülten yüklenemedi.');
      setBulletin(null);
      setForm(emptyForm);
    } finally {
      setLoading(false);
    }
  }, [bulletinType, getAuthHeader]);

  useEffect(() => { fetchBulletin(); }, [fetchBulletin]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/admin/bulletin`, { ...form, bulletin_type: bulletinType }, { headers: getAuthHeader() });
      toast.success('Bülten kaydedildi.');
      fetchBulletin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bülten kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/admin/bulletin/${bulletinType}`, { headers: getAuthHeader() });
      toast.success('Bülten silindi.');
      setBulletin(null);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bülten silinemedi.');
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    try { return format(new Date(isoStr), 'd MMMM yyyy HH:mm', { locale: tr }); } catch { return isoStr; }
  };

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <>
      {bulletin && (
        <Card className="border-[#E6DCCF] bg-white mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#7A6A5C] font-medium">Mevcut Aktif Bülten</CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-[#7A6A5C] space-y-1">
            <p><span className="font-semibold text-[#2E2620]">Referans:</span> {bulletin.reference_symbol}</p>
            <p><span className="font-semibold text-[#2E2620]">Benzer Hisse:</span> {bulletin.similar_symbol}</p>
            <p><span className="font-semibold text-[#2E2620]">Son Güncelleme:</span> {formatDate(bulletin.updated_at || bulletin.created_at)}</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E6DCCF]">
        <CardHeader>
          <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">
            {bulletin ? 'Bülteni Güncelle' : 'Yeni Bülten Oluştur'}
          </CardTitle>
          <CardDescription className="text-[#7A6A5C]">
            Her kayıt mevcut bülteni günceller — her tür için tek aktif bülten vardır.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading ? (
            <div className="flex items-center gap-2 text-[#7A6A5C]">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Yükleniyor...</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#2E2620]">Referans Hisse Sembolü</Label>
                  <Input
                    value={form.reference_symbol}
                    onChange={(e) => handleChange('reference_symbol', e.target.value.toUpperCase())}
                    placeholder="ör. THYAO"
                    className="border-[#E6DCCF] focus-visible:ring-[#C86F4A]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#2E2620]">Benzer Hisse Sembolü</Label>
                  <Input
                    value={form.similar_symbol}
                    onChange={(e) => handleChange('similar_symbol', e.target.value.toUpperCase())}
                    placeholder="ör. PGSUS"
                    className="border-[#E6DCCF] focus-visible:ring-[#C86F4A]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#2E2620]">Zaman Aralığı</Label>
                  <Select value={form.interval} onValueChange={(v) => handleChange('interval', v)}>
                    <SelectTrigger className="border-[#E6DCCF]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1d">Günlük</SelectItem>
                      <SelectItem value="1wk">Haftalık</SelectItem>
                      <SelectItem value="1mo">Aylık</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#2E2620]">Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="border-[#E6DCCF] focus-visible:ring-[#C86F4A]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#2E2620]">Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => handleChange('end_date', e.target.value)}
                    className="border-[#E6DCCF] focus-visible:ring-[#C86F4A]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-[#2E2620]">Admin Yorumu</Label>
                <Textarea
                  value={form.admin_comment}
                  onChange={(e) => handleChange('admin_comment', e.target.value)}
                  placeholder="Kullanıcılara gösterilecek analiz yorumunuzu yazın..."
                  rows={5}
                  className="border-[#E6DCCF] focus-visible:ring-[#C86F4A] resize-none"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Kaydediliyor...</> : <><Save className="w-4 h-4 mr-2" />Kaydet</>}
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchBulletin}
                  disabled={loading}
                  className="border-[#E6DCCF] text-[#2E2620] hover:bg-[#F6F1EA] rounded-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Yenile
                </Button>
                {bulletin && (
                  <Button
                    variant="outline"
                    onClick={() => setDeleteOpen(true)}
                    disabled={deleting}
                    className="border-red-200 text-red-600 hover:bg-red-50 rounded-full ml-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Bülteni Sil
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="border-[#E6DCCF] bg-[#F6F1EA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#2E2620] font-['Playfair_Display']">
              Bülteni sil?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#7A6A5C]">
              Mevcut bülten kalıcı olarak silinecek. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E6DCCF]">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const AdminBulletinPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('simons');
  const activeTabInfo = TABS.find((t) => t.key === activeTab);

  return (
    <div className="flex min-h-screen bg-[#F6F1EA]">
      <Sidebar />
      <main className="flex-1 lg:ml-64 px-4 sm:px-6 lg:px-8 py-8 mt-14 lg:mt-0">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#C86F4A]/10 flex items-center justify-center">
              <Newspaper className="w-5 h-5 text-[#C86F4A]" />
            </div>
            <div>
              <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#2E2620]">
                Bülten Yönetimi
              </h1>
              <p className="text-sm text-[#7A6A5C]">
                Kullanıcılara yönelik aylık analiz bültenlerini yönetin.
              </p>
            </div>
          </div>

          {/* Tab selector */}
          <div className="flex bg-white border border-[#E6DCCF] rounded-full p-1 gap-1 w-fit">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeTab === key
                    ? 'bg-[#2E2620] text-white'
                    : 'text-[#7A6A5C] hover:text-[#2E2620]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <p className="text-xs text-[#7A6A5C] -mt-3">
            {activeTab === 'simons'
              ? 'Simons Analizi yalnızca Platinum üyelere görünür.'
              : 'BIST Analizi tüm kullanıcılara görünür.'}
          </p>

          <BulletinForm key={activeTab} bulletinType={activeTab} getAuthHeader={getAuthHeader} />
        </div>
      </main>
    </div>
  );
};

export default AdminBulletinPage;
